<template>
  <CustomDialog
    ref="customDialog"
    :title="_title"
    :subtitle="_subtitle"
    :footerButtons="_buttons"
    :loading="loading"
    @submit="submit()"
    @opened="showForm = true"
    @closed="showForm = false"
  >
    <div>
      <v-form
        v-if="!!showForm"
        v-model="valid"
        ref="form"
        class="mt-4"
        :disabled="loading"
      >
        <v-row no-gutters>
          <v-col cols="12">
            <v-text-field
              v-model="form.name"
              :label="`Nome do ${$t('desk')}`"
              :rules="_rules"
              :disabled="_viewMode"
              outlined
            />
          </v-col>

          <v-col cols="12" md="6" class="pr-md-2">
            <ItemSelector
              v-model="sections.groups.selected"
              :items="sections.groups.data"
              :label="sections.groups.label"
              :placeholder="sections.groups.placeholder"
              :count="sections.groups.count"
              :item-value="sections.groups.itemValue"
              :item-text="sections.groups.itemText"
              :multiple="sections.groups.multiple"
              :loading="loading"
              :disabled="_viewMode || (_isSupervisor && _editMode)"
              :required="true"
              @input="handleSelectedGroup"
              @search="handleSearchGroups"
              @paginate="fetchMoreGroups"
            />
          </v-col>

          <v-col cols="12" md="6" class="pl-md-2 mt-6 mt-md-0">
            <ItemSelector
              v-model="sections.subgroups.selected"
              :items="sections.subgroups.data"
              :label="sections.subgroups.label"
              :placeholder="sections.subgroups.placeholder"
              :count="sections.subgroups.count"
              :item-value="sections.subgroups.itemValue"
              :item-text="sections.subgroups.itemText"
              :multiple="sections.subgroups.multiple"
              :loading="loading"
              :disabled="
                _viewMode || !form.comarcaID || (_isSupervisor && _editMode)
              "
              :required="true"
              @input="handleSelectedSubgroup"
              @search="handleSearchSubgroups"
              @paginate="fetchMoreSubgroups"
            />
          </v-col>

          <v-col cols="12" class="mt-6">
            <ItemSelector
              v-model="sections.supervisor.selected"
              :items="sections.supervisor.data"
              :label="sections.supervisor.label"
              :placeholder="sections.supervisor.placeholder"
              :count="sections.supervisor.count"
              :item-value="sections.supervisor.itemValue"
              :item-text="sections.supervisor.itemText"
              :item-sub-text="sections.supervisor.itemSubText"
              :multiple="sections.supervisor.multiple"
              :loading="loading"
              :disabled="_viewMode"
              :required="false"
              @input="handleSelectedSupervisor"
              @search="handleSearchSupervisor"
              @paginate="fetchMoreSupervisor"
            />
          </v-col>

          <v-col cols="12" class="mt-6">
            <ItemSelector
              v-model="sections.people.selected"
              :items="sections.people.data"
              :label="sections.people.label"
              :placeholder="sections.people.placeholder"
              :count="sections.people.count"
              :item-value="sections.people.itemValue"
              :item-text="sections.people.itemText"
              :item-sub-text="sections.people.itemSubText"
              :multiple="sections.people.multiple"
              :loading="loading"
              :disabled="_viewMode"
              :required="false"
              @input="handleSelectedPeople"
              @search="handleSearchPeople"
              @paginate="fetchMorePeople"
            />
          </v-col>

          <v-col cols="12" class="mt-6">
            <v-select
              v-model="form.enableRequesterChat"
              :items="_requesterChatOptions"
              :label="$t('requesterChat.label')"
              :hint="$t('requesterChat.hintDesk')"
              :disabled="_viewMode || (!_canEdit && !_canCreate)"
              outlined
            />
          </v-col>

          <v-col cols="12" class="mt-6" v-if="false">
            <v-switch
              v-model="form.externalRating"
              label="Pesquisa de satisfação (URL externa)"
              class="mt-0"
              :disabled="_viewMode || (!_canEdit && !_canCreate)"
            />
          </v-col>

          <v-col v-if="form.externalRating" cols="12" md="12">
            <v-text-field
              v-model="form.externalRatingURL"
              label="URL da pesquisa de satisfação"
              :rules="_rules"
              :disabled="_viewMode || (!_canEdit && !_canCreate)"
              outlined
            />
          </v-col>

          <v-col cols="12" md="12">
            <div class="d-flex flex-shrink">
              <v-switch
                v-model="form.isActive"
                :label="`Ativar / Desativar ${$t('desk')}`"
                :disabled="_viewMode || (!_canEdit && !_canCreate)"
              />
            </div>
          </v-col>

          <v-col v-if="!_editMode && !_viewMode" cols="12" md="12">
            <!-- <v-switch
            v-model="form.createCalendar"
            label="Criar calendário"
            class="mt-0"
          /> -->
          </v-col>
        </v-row>
      </v-form>

      <CustomDialog
        ref="confirmDialog"
        title="Confirmação de alteração"
        subtitle="Deseja realmente salvar estas alterações?"
        width="800"
        :footerButtons="_confirmButtons"
        @submit="submitConfirmation(true)"
        @close="submitConfirmation(false)"
      >
        <div>
          <p v-if="addedSupervisors.length">
            • Adição dos Supervisores: {{ addedSupervisors.join(", ") }}
          </p>

          <p v-if="removedSupervisors.length">
            • Remoção dos Supervisores: {{ removedSupervisors.join(", ") }}
          </p>

          <p v-if="addedUsers.length">
            • Adição dos Atendentes: {{ addedUsers.join(", ") }}
          </p>

          <p v-if="removedUsers.length">
            • Remoção dos Atendentes: {{ removedUsers.join(", ") }}
          </p>
        </div>
      </CustomDialog>
    </div>
  </CustomDialog>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { createCounters, updateCounters } from "@/services/counters";
import { getJurisdictions } from "@/services/jurisdictions";
import { getCourts } from "@/services/courts";
import {
  handleAlert,
  processItemSelectorData,
  processItemSelectorInitialData,
} from "@/utils/index";
import { validation } from "@/utils/validators";
import CustomDialog from "@/components/customDialog/CustomDialog.vue";
import { getUsers } from "@/services/users";
import { getPermissionGroups } from "@/services/permissions";
import ItemSelector from "@/components/itemSelector/ItemSelector.vue";
import { requesterChatOptions } from "@/utils/requesterChat";

export default {
  name: "CounterDialog",

  components: { CustomDialog, ItemSelector },

  data() {
    return {
      showForm: false,
      mode: "create",
      valid: false,
      loading: false,
      operatorGroupIDs: [],
      supervisorGroupIDs: [],
      courts: [],
      name: "",
      jurisdictions: [],
      form: {
        _id: null,
        name: "",
        comarcaID: "",
        varaID: "",
        peopleIDs: [],
        supervisorIDs: [],
        externalRating: false,
        enableRequesterChat: null,
        externalRatingURL: "",
        isActive: false,
        createCalendar: false,
        counterType: "notary",
        operationMode: "queue",
        averageServiceTime: 15,
        delayLimit: 15,
      },
      sections: {
        counterType: {
          label: "Tipo do balcão",
          placeholder: "Selecione o tipo...",
          itemValue: "value",
          itemText: "label",
          multiple: false,
          loading: false,
          data: [
            { value: "notary", label: "Cartório" },
            { value: "cabinet", label: "Gabinete" },
          ],
          selected: { value: "notary", label: "Cartório" },
          page: 0,
          count: 2,
        },
        operationMode: {
          label: "Modo de operação",
          placeholder: "Selecione o modo...",
          itemValue: "value",
          itemText: "label",
          multiple: false,
          loading: false,
          data: [
            { value: "queue", label: "Fila" },
            { value: "schedule", label: "Agendamento" },
          ],
          selected: { value: "queue", label: "Fila" },
          page: 0,
          count: 2,
        },
        people: {
          label: "Atendentes",
          placeholder: "Digite para buscar...",
          itemValue: "_id",
          itemText: "name",
          itemSubText: "email",
          multiple: true,
          loading: false,
          data: [],
          selected: [],
          page: 0,
          count: 0,
        },
        supervisor: {
          label: "Supervisor",
          placeholder: "Digite para buscar...",
          itemValue: "_id",
          itemText: "name",
          itemSubText: "email",
          multiple: true,
          loading: false,
          data: [],
          selected: [],
          page: 0,
          count: 0,
        },
        subgroups: {
          label: this.$t("subgroup"),
          placeholder: `Selecione a ${this.$t("subgroup")}...`,
          itemValue: "_id",
          itemText: "name",
          multiple: false,
          loading: false,
          data: [],
          selected: null,
          page: 0,
          count: 0,
        },
        groups: {
          label: this.$t("group"),
          placeholder: `Selecione o ${this.$t("group")}...`,
          itemValue: "_id",
          itemText: "name",
          multiple: false,
          loading: false,
          data: [],
          selected: null,
          page: 0,
          count: 0,
        },
      },
      attendants: [],
      supervisors: [],
      addedUsers: [],
      removedUsers: [],
      addedSupervisors: [],
      removedSupervisors: [],
    };
  },

  watch: {
    ["sections.people.selected"]: {
      handler(val) {
        const newIDs = val.map((user) => user._id);
        const originalIDs = this.attendants.map((user) => user._id);

        const added = val.filter((user) => !originalIDs.includes(user._id));
        const removed = this.attendants.filter(
          (user) => !newIDs.includes(user._id)
        );

        this.addedUsers = added.map((user) => user.name || user.email);
        this.removedUsers = removed.map((user) => user.name || user.email);
      },
      deep: true,
    },

    ["sections.supervisor.selected"]: {
      handler(val) {
        const newIDs = val.map((user) => user._id);
        const originalIDs = this.supervisors.map((user) => user._id);

        const added = val.filter((user) => !originalIDs.includes(user._id));
        const removed = this.supervisors.filter(
          (user) => !newIDs.includes(user._id)
        );

        this.addedSupervisors = added.map((user) => user.name || user.email);
        this.removedSupervisors = removed.map(
          (user) => user.name || user.email
        );
      },
      deep: true,
    },
  },

  computed: {
    _requesterChatOptions() {
      return requesterChatOptions({
        inherit: this.$t("requesterChat.inheritFromDesk"),
        allow: this.$t("requesterChat.allow"),
        block: this.$t("requesterChat.block"),
      });
    },

    ...mapState(["user"]),
    ...mapGetters(["attendanceRole"]),

    _isAdmin() {
      return this.attendanceRole === "admin";
    },

    _isSupervisor() {
      return this.attendanceRole === "supervisor";
    },

    _canCreate() {
      return this.user.permissions?.includes("desks.create");
    },

    _canEdit() {
      return this.user.permissions?.includes("desks.edit");
    },

    _viewMode() {
      return this.mode === "view";
    },

    _editMode() {
      return this.mode === "edit";
    },

    _title() {
      if (this._viewMode) return `Visualizar ${this.$t("desk")}`;
      else if (this._editMode) return `Editar ${this.$t("desk")}`;

      return `Adicionar ${this.$t("desk")}`;
    },

    _subtitle() {
      if (this._viewMode) return "";
      if (this._editMode)
        return `Preencha os campos abaixo para editar ${this.$t("desk")}`;

      return `Preencha os campos abaixo para cadastrar ${this.$t("desk")}`;
    },

    _buttons() {
      if (this._viewMode)
        return [
          {
            label: "Fechar",
            color: "primary",
            action: "close",
          },
        ];

      return [
        {
          label: "Cancelar",
          color: "secondary",
          action: "close",
        },
        {
          label: "Finalizar",
          color: "primary",
          action: "submit",
        },
      ];
    },

    _confirmButtons() {
      return [
        {
          label: "Cancelar",
          color: "secondary",
          action: "close",
        },
        {
          label: "Confirmar",
          color: "primary",
          action: "submit",
        },
      ];
    },

    _rules() {
      return [
        (v) => (!!v && v.length > 0) || "Campo obrigatório",
        (v) => validation(v) || "Informe um valor válido",
      ];
    },
  },

  methods: {
    async submit() {
      if (!this.$refs.form.validate() || this._viewMode) {
        return;
      }

      if (
        this.addedSupervisors.length ||
        this.removedSupervisors.length ||
        this.addedUsers.length ||
        this.removedUsers.length
      ) {
        this.$refs.confirmDialog.open();
        return;
      }

      this.handleCounter();
    },

    async handleCounter() {
      this.loading = true;

      if (this._editMode) await this.updateCounters();
      else await this.createCounters();

      this.loading = false;
    },

    submitConfirmation(confirm = true) {
      this.$refs.confirmDialog.close();

      if (confirm) this.handleCounter();
    },

    async createCounters() {
      try {
        const params = {
          createCalendar: this.form.createCalendar,
        };

        const payload = {
          name: this.form.name,
          comarcaID: this.form.comarcaID,
          varaID: this.form.varaID,
          peopleIDs: [...this.form.peopleIDs, ...this.form.supervisorIDs],
          externalRating: this.form.externalRating,
          // `null` = não configurado neste nível; a resolução volta a cair para
          // vara e comarca. Ver ADR-008.
          enableRequesterChat: this.form.enableRequesterChat,
          ...(this.form.externalRating && {
            externalRatingURL: this.form.externalRatingURL,
          }),
          isActive: this.form.isActive,
          counterType: this.form.counterType,
          ...(this.form.counterType === "cabinet" && {
            operationMode: this.form.operationMode,
            averageServiceTime: this.form.averageServiceTime,
            ...(this.form.operationMode === "schedule" && {
              delayLimit: this.form.delayLimit,
            }),
          }),
        };

        await createCounters(params, payload);

        this.handleAlert(`${this.$t("desk")} criado com sucesso`, "success");
        this.$emit("success");

        this.closeDialog();
      } catch (error) {
        console.log(error);
        this.handleAlert(error?.data?.message, "error");
      }
    },

    async updateCounters() {
      try {
        const payload = {
          name: this.form.name,
          comarcaID: this.form.comarcaID,
          varaID: this.form.varaID,
          peopleIDs: [...this.form.peopleIDs, ...this.form.supervisorIDs],
          externalRating: this.form.externalRating,
          // `null` = não configurado neste nível; a resolução volta a cair para
          // vara e comarca. Ver ADR-008.
          enableRequesterChat: this.form.enableRequesterChat,
          ...(this.form.externalRating && {
            externalRatingURL: this.form.externalRatingURL,
          }),
          isActive: this.form.isActive,
          counterType: this.form.counterType,
          ...(this.form.counterType === "cabinet" && {
            operationMode: this.form.operationMode,
            averageServiceTime: this.form.averageServiceTime,
            ...(this.form.operationMode === "schedule" && {
              delayLimit: this.form.delayLimit,
            }),
          }),
        };

        await updateCounters(this.form._id, payload);

        this.handleAlert(`${this.$t("desk")} editado com sucesso`, "success");
        this.$emit("success");

        this.closeDialog();
      } catch (error) {
        console.log(error);
        this.handleAlert(error?.data?.message, "error");
      }
    },

    async getJurisdictions() {
      try {
        const { data } = await getJurisdictions();
        this.jurisdictions = data;
      } catch (error) {
        this.handleAlert(error?.data?.message, "error");
      }
    },

    async getCourtsById(id) {
      this.form.comarcaID = id;
      try {
        const payload = {
          comarcaID: id,
        };
        const { data } = await getCourts(payload);
        this.courts = data;
      } catch (error) {
        console.log(error);
        this.handleAlert(error?.data?.message, "error");
      }
    },

    async searchCourts(name) {
      if (!this.form.comarcaID) return;
      if (!name) return;
      try {
        const payload = {
          comarcaID: this.form.comarcaID,
          name,
        };
        const { data } = await getCourts(payload);
        this.courts = data;
      } catch (error) {
        console.log(error);
        this.handleAlert(error?.data?.message, "error");
      }
    },

    async openDialog(data, mode) {
      this.mode = mode || "create";

      this.resetForm();

      if (data) {
        const supervisors = (data?.people || []).map((el) => ({
          _id: el._id,
          name: el.name,
          email: el.email,
        }));

        this.supervisors = supervisors;

        const attendants = data?.attendants?.map((el) => ({
          _id: el._id,
          name: el.name,
          email: el.email,
        }));

        this.attendants = attendants;

        this.sections.supervisor.page = 0;
        this.sections.supervisor.count = 0;
        this.sections.supervisor.loading = false;
        this.sections.supervisor.data = JSON.parse(JSON.stringify(supervisors));
        this.sections.supervisor.selected = JSON.parse(
          JSON.stringify(supervisors)
        );

        this.sections.people.page = 0;
        this.sections.people.count = 0;
        this.sections.people.loading = false;
        this.sections.people.data = JSON.parse(JSON.stringify(attendants));
        this.sections.people.selected = JSON.parse(JSON.stringify(attendants));

        this.sections.groups.page = 0;
        this.sections.groups.count = 0;
        this.sections.groups.loading = false;
        this.sections.groups.data = data.comarca
          ? [{ _id: data.comarca._id, name: data.comarca.name }]
          : [];
        this.sections.groups.selected = data.comarca
          ? { _id: data.comarca._id, name: data.comarca.name }
          : null;

        this.processItemSelectorInitialData(this.sections.groups, data.comarca);

        this.sections.subgroups.page = 0;
        this.sections.subgroups.count = 0;
        this.sections.subgroups.loading = false;
        this.sections.subgroups.data = data.vara
          ? [{ _id: data.vara._id, name: data.vara.name }]
          : [];
        this.sections.subgroups.selected = data.vara
          ? { _id: data.vara._id, name: data.vara.name }
          : null;

        this.form = {
          _id: data._id,
          name: data.name,
          comarcaID: data.comarca._id,
          varaID: data.vara._id,
          peopleIDs: (data?.attendants || []).map((el) => el._id),
          supervisorIDs: (data?.people || []).map((el) => el._id),
          externalRating: data.externalRating,
          externalRatingURL: data.externalRating ? data.externalRatingURL : "",
          enableRequesterChat: data.enableRequesterChat ?? null,
          isActive: data.isActive,
          counterType: data.counterType || "notary",
          operationMode: data.operationMode || "queue",
          averageServiceTime: data.averageServiceTime || 15,
          delayLimit: data.delayLimit || 15,
        };

        this.sections.counterType.selected =
          this.sections.counterType.data.find(
            (item) => item.value === this.form.counterType
          ) || { value: "notary", label: "Cartório" };

        this.sections.operationMode.selected =
          this.sections.operationMode.data.find(
            (item) => item.value === this.form.operationMode
          ) || { value: "queue", label: "Fila" };
      }

      await this.getPermissionGroups();

      await Promise.all([
        this.handleFetchGroups(),
        this.handleFetchSubgroups(),
        this.handleFetchPeople(),
        this.handleFetchSupervisor(),
      ]);

      this.$refs.customDialog.open();
    },

    closeDialog() {
      this.$refs.customDialog.close();
    },

    async getPermissionGroups() {
      if (!this.$can("attendances")) return;

      try {
        const payload = {
          module: "attendances",
          type: "role",
        };

        const { data } = await getPermissionGroups(payload);

        this.operatorGroupIDs =
          data.filter((el) => el.role === "operator").map((el) => el._id) || [];

        this.supervisorGroupIDs =
          data.filter((el) => el.role === "supervisor").map((el) => el._id) ||
          [];
      } catch (error) {
        this.handleAlert(error?.data?.message, "error");
      }
    },

    resetForm() {
      this.loading = false;
      this.form._id = "";
      this.form.name = "";
      this.form.comarcaID = "";
      this.form.varaID = "";
      this.form.peopleIDs = [];
      this.form.supervisorIDs = [];
      this.form.externalRating = false;
      this.form.externalRatingURL = "";
      this.form.enableRequesterChat = null;
      this.form.isActive = false;
      this.form.createCalendar = false;
      this.form.counterType = "notary";
      this.form.operationMode = "queue";
      this.form.averageServiceTime = 15;
      this.form.delayLimit = 15;

      this.sections.groups.selected = null;
      this.sections.subgroups.selected = null;
      this.sections.people.selected = [];
      this.sections.supervisor.selected = [];
      this.sections.counterType.selected = {
        value: "notary",
        label: "Cartório",
      };
      this.sections.operationMode.selected = { value: "queue", label: "Fila" };

      this.addedUsers = [];
      this.removedUsers = [];
      this.attendants = [];

      if (this.$refs.form) this.$refs.form.resetValidation();
    },

    async handleFetchPeople(search = "") {
      this.sections.people.loading = true;

      const payload = {
        sortBy: "email",
        sortOrder: "asc",
        limit: 10,
        offset: this.sections.people.page * 10,
        permissionGroupIDs: this.operatorGroupIDs,
        ...(!!search?.length && { search }),
        nin: this.sections.people.selected.map((el) => el._id), // Busca as pessoas com exclusão das selecionadas (nin = not in)
      };
      const {
        data,
        headers: { "x-count": count },
      } = await getUsers(payload);

      if (search) {
        this.sections.people.data = [
          ...this.sections.people.selected,
          ...data.map((person) => ({
            _id: person._id,
            name: person.name || person.email,
            email: person.email,
          })),
          ...this.sections.people.data,
        ];
      } else {
        this.sections.people.data = [
          ...this.sections.people.selected,
          ...this.sections.people.data,
          ...data.map((person) => ({
            _id: person._id,
            name: person.name || person.email,
            email: person.email,
          })),
        ];
      }

      this.sections.people.count = Number(count);
      this.sections.people.loading = false;
    },

    handleSelectedPeople(selected) {
      this.sections.people.selected = selected;
      this.form.peopleIDs = selected.map((el) => el._id);
    },

    async handleSearchPeople(search = "") {
      this.sections.people.page = 0;
      this.sections.people.count = 0;
      this.sections.people.data = [];

      this.handleFetchPeople(search);
    },

    async fetchMorePeople(search = "") {
      this.sections.people.page++;
      this.handleFetchPeople(search);
    },

    ////
    async handleFetchSupervisor(search = "") {
      this.sections.supervisor.loading = true;

      const payload = {
        sortBy: "_id",
        sortOrder: "asc",
        limit: 10,
        offset: this.sections.supervisor.page * 10,
        ...(this.supervisorGroupIDs?.length && {
          permissionGroupIDs: this.supervisorGroupIDs,
        }),
        ...(!!search?.length && { search }),
        nin: this.sections.supervisor.selected.map((el) => el._id), // Busca as pessoas com exclusão das selecionadas (nin = not in)
      };
      const {
        data,
        headers: { "x-count": count },
      } = await getUsers(payload);

      this.sections.supervisor.data = [
        ...data.map((person) => ({
          _id: person._id,
          name: person.name || person.email,
          email: person.email,
        })),
        ...this.sections.supervisor.selected,
        ...this.sections.supervisor.data,
      ];
      this.sections.supervisor.count = Number(count);
      this.sections.supervisor.loading = false;
    },

    handleSelectedSupervisor(selected) {
      this.sections.supervisor.selected = selected;
      this.form.supervisorIDs = selected.map((el) => el._id);
    },

    async handleSearchSupervisor(search = "") {
      this.sections.supervisor.page = 0;
      this.sections.supervisor.count = 0;
      this.sections.supervisor.data = [];

      this.handleFetchSupervisor(search);
    },

    async fetchMoreSupervisor(search = "") {
      this.sections.supervisor.page++;
      this.handleFetchSupervisor(search);
    },
    //////

    async handleFetchSubgroups(search = "") {
      if (!this.form.comarcaID) {
        this.sections.subgroups.loading = false;
        return;
      }

      this.sections.subgroups.loading = true;

      const payload = {
        sortBy: "name",
        sortOrder: "asc",
        limit: 10,
        offset: this.sections.subgroups.page * 10,
        ...(!!search?.length && { search }),
        ...(!!this.sections.subgroups.selected && {
          nin: [this.sections.subgroups.selected._id], // Busca com exclusão das selecionadas (nin = not in)
        }),
        comarcaID: this.form.comarcaID,
      };

      const {
        data,
        headers: { "x-count": count },
      } = await getCourts(payload);

      const oldData = JSON.parse(JSON.stringify(this.sections.subgroups.data));

      this.sections.subgroups.data = [];

      if (this.sections.subgroups.selected) {
        this.sections.subgroups.data.push(this.sections.subgroups.selected);
      }

      if (oldData.length > 0) {
        this.sections.subgroups.data.push(...oldData);
      }

      if (data.length > 0) {
        this.sections.subgroups.data.push(
          ...data.map((subgroup) => ({
            _id: subgroup._id,
            name: subgroup.name,
          }))
        );
      }

      this.sections.subgroups.count = Number(count);
      this.sections.subgroups.loading = false;
    },

    handleSelectedSubgroup(selected) {
      this.sections.subgroups.selected = selected;
      this.form.varaID = selected?._id || null;
    },

    async handleSearchSubgroups(search = "") {
      this.sections.subgroups.page = 0;
      this.sections.subgroups.count = 0;
      this.sections.subgroups.data = [];

      this.handleFetchSubgroups(search);
    },

    async fetchMoreSubgroups(search = "") {
      this.sections.subgroups.page++;
      this.handleFetchSubgroups(search);
    },

    async handleFetchGroups(search = "") {
      this.sections.groups.loading = true;

      const payload = {
        sortBy: "name",
        sortOrder: "asc",
        limit: 10,
        offset: this.sections.groups.page * 10,
        ...(!!search?.length && { search }),
        // ...(!!this.sections.groups.selected && {
        //   nin: [this.sections.groups.selected._id], // Busca com exclusão das selecionadas (nin = not in)
        // }),
      };
      const {
        data,
        headers: { "x-count": count },
      } = await getJurisdictions(payload);

      this.processItemSelectorData(this.sections.groups, data, {
        override: false,
      });

      this.sections.groups.count = Number(count);
      this.sections.groups.loading = false;
    },

    handleSelectedGroup(selected) {
      this.sections.groups.selected = selected;
      this.form.comarcaID = selected?._id || null;
      this.sections.subgroups.selected = null;

      this.handleSearchSubgroups("");
    },

    async handleSearchGroups(search = "") {
      this.sections.groups.page = 0;
      this.sections.groups.count = 0;
      this.sections.groups.data = [];

      this.handleFetchGroups(search);
    },

    async fetchMoreGroups(search = "") {
      this.sections.groups.page++;
      this.handleFetchGroups(search);
    },

    handleSelectedCounterType(selected) {
      this.sections.counterType.selected = selected;
      this.form.counterType = selected?.value || "notary";

      if (this.form.counterType === "notary") {
        this.form.operationMode = "queue";
        this.sections.operationMode.selected = {
          value: "queue",
          label: "Fila",
        };
      }
    },

    handleSelectedOperationMode(selected) {
      this.sections.operationMode.selected = selected;
      this.form.operationMode = selected?.value || "queue";
    },

    handleAlert,
    processItemSelectorData,
    processItemSelectorInitialData,
  },
};
</script>

<style src="./style.scss" lang="scss" scoped />
